import type { ReactNode } from "react";
import { ChevronsUpDown as ChevronsUpDownIcon, Plus as PlusIcon } from "@tamagui/lucide-icons";
import { Avatar, Badge, Body3, Subtitle1, XStack, YStack } from "@medbillai/ui";
import { analyticsService } from "../../../lib/analytics";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { firstName, planEndedText, planSubtitle } from "../format";
import { PressableStack } from "../PressableStack";
import type { CoveragePlan } from "../read/readCoverage";
import { TierPicker, useOpenedTier } from "../where-you-stand/TierPicker";
import { shownTier, standingOf } from "../where-you-stand/tiers";
import { headlineState } from "./HeadlineCard";
type PlanHeaderProps = {
  plan: CoveragePlan;
  onPress: () => void;
  badgeless?: boolean;
};
export function PlanHeader({
  plan,
  onPress,
  badgeless = false
}: PlanHeaderProps) {
  const pickTier = useCoveragePlanStore(state => state.pickTier);
  const detailsSaved = useCoveragePlanStore(state => state.detailsSaved === plan.membershipId);
  const standing = standingOf(plan);
  const tier = shownTier(standing, useCoveragePlanStore(state => state.tier));
  const openedOn = useOpenedTier(plan.membershipId, tier);
  const cardShowsTotals = headlineState(plan).kind === "totals";
  const pickable = standing.switchable && cardShowsTotals;
  const header = <HeaderSlot label={`${plan.planName ?? plan.payerName}, switch plan`} mark={<PayerMark name={plan.payerName} logoUrl={plan.logoUrl} />} title={plan.planName ?? plan.payerName} subtitle={pickable ? planSubtitle(plan) : [firstName(plan), planSubtitle(plan)].filter(Boolean).join(" · ")} below={plan.ended ? <Badge size="small" bordered={false} alignSelf="flex-start" marginTop="$sp0.5">
            {planEndedText(plan)}
          </Badge> : null} chevron onPress={onPress}>
      {plan.health !== "ok" && !plan.ended && !badgeless ? <Badge size="small" bordered={false} status={detailsSaved ? "neutral" : "warning"}>
          Couldn’t confirm
        </Badge> : null}
    </HeaderSlot>;
  if (!pickable) {
    return header;
  }
  return <XStack alignItems="center" gap="$sp1" data-sentry-element="XStack" data-sentry-component="PlanHeader" data-sentry-source-file="PlanHeader.tsx">
      <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="PlanHeader.tsx">{header}</YStack>
      <TierPicker plan={plan} tone="light" figures={plan.inNetwork} standing={standing} tier={tier} onPick={t => {
      if (t === tier) {
        return;
      }
      analyticsService.trackEvent("CoverageTierPicked", {
        value: t,
        default: openedOn
      });
      pickTier(t);
    }} data-sentry-element="TierPicker" data-sentry-source-file="PlanHeader.tsx" />
    </XStack>;
}
export function AddInsuranceHeader({
  onPress
}: {
  onPress: () => void;
}) {
  return <HeaderSlot label="Add insurance" mark={<Avatar size="small" shape="square" icon={PlusIcon} aria-hidden />} title="Add insurance" subtitle="No insurance on file" onPress={onPress} data-sentry-element="HeaderSlot" data-sentry-component="AddInsuranceHeader" data-sentry-source-file="PlanHeader.tsx" />;
}
function HeaderSlot({
  label,
  mark,
  title,
  subtitle,
  below,
  chevron = false,
  onPress,
  children
}: {
  label: string;
  mark: ReactNode;
  title: string;
  subtitle: string;
  below?: ReactNode;
  chevron?: boolean;
  onPress: () => void;
  children?: ReactNode;
}) {
  return <PressableStack aria-label={label} onPress={onPress} alignItems="center" gap="$sp1" paddingLeft="$sp0.75" minHeight="$si5.5" data-sentry-element="PressableStack" data-sentry-component="HeaderSlot" data-sentry-source-file="PlanHeader.tsx">
      {mark}
      <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="PlanHeader.tsx">
        <XStack alignItems="center" gap="$sp0.5" data-sentry-element="XStack" data-sentry-source-file="PlanHeader.tsx">
          <Subtitle1 color="$textDefault" numberOfLines={1} flexShrink={1} data-sentry-element="Subtitle1" data-sentry-source-file="PlanHeader.tsx">
            {title}
          </Subtitle1>
          {chevron ? <ChevronsUpDownIcon size="$iconXSmall" color="$iconSubtle" /> : null}
        </XStack>
        {subtitle ? <Body3 color="$textSubtle">{subtitle}</Body3> : null}
        {below}
      </YStack>
      {children}
    </PressableStack>;
}

/** The payer's logo, or its initial when the catalog has none or it fails to load. */
export function PayerMark({
  name,
  logoUrl,
  size
}: {
  name: string;
  logoUrl: string | null;
  /** Below the DS's smallest avatar, for a mark inline with body text. */
  size?: "$si3";
}) {
  return <Avatar size="small" shape="square" {...size ? {
    width: size,
    height: size
  } : {}} image={logoUrl ?? undefined} initials={name.charAt(0).toUpperCase()} aria-hidden data-sentry-element="Avatar" data-sentry-component="PayerMark" data-sentry-source-file="PlanHeader.tsx" />;
}